import { type RefObject, useLayoutEffect, useState } from 'react';

// Figma steps the two values through 18 / 16 / 14; the rest are a legibility tail for balances the
// design never sized, on headers narrower than it assumed.
const VALUE_SIZES = [18, 16, 14, 12, 11, 10];

// The dual row can want more width than a narrow header has. The store "+" is anchored and the two
// value slots absorb the squeeze by clipping, so the row never escapes the panel — but a clipped
// balance is unreadable, and this is what keeps the clip from being reached: the value size steps
// down until both slots hold their whole value, the web stand-in for native `adjustsFontSizeToFit`.
// Past the last size the value clips: the balance is always shown in full, never abbreviated.
//
// The slots clip (`overflow: hidden`), which is what makes `scrollWidth` a dependable read of how
// much width the value actually wants. Measuring the pill instead would report a fit at every size,
// because flex shrinks the pill to the panel and moves the overflow inside these slots.
export function useFitBalanceValueSize(
  cashSlot: RefObject<HTMLElement | null>,
  coinSlot: RefObject<HTMLElement | null>,
  resetKey: string,
  enabled: boolean,
) {
  const [sizeIndex, setSizeIndex] = useState(0);
  // Bumped on every container resize. Resetting `sizeIndex` alone is not enough: when it is already
  // 0 (the balance fit the wide layout) setting it to 0 is a no-op, no re-render happens, and the
  // fit never re-runs for the narrower layout — e.g. landscape -> portrait on mobile, which left
  // both values clipped on either side.
  const [fitVersion, setFitVersion] = useState(0);

  // A new value, or a header that changed width, deserves the largest size again — otherwise the row
  // would keep whatever a previous, longer balance drove it down to.
  useLayoutEffect(() => {
    setSizeIndex(0);
  }, [resetKey]);

  // Re-fits when the header or the panel changes width. The container alone is not enough: the panel
  // animates its own width (`transition: all`) after the container has already jumped, so a fit run
  // on the container's resize reads the panel mid-transition and settles on the wrong size, with
  // nothing left to trigger another run once the transition ends. Only width is acted on — a font
  // step changes the panel's height, and treating that as a resize would feed every step back in as
  // a reset. The dual row's width never depends on the value size (pinned on desktop, 100% of the
  // header on mobile), so watching the panel is safe here.
  useLayoutEffect(() => {
    const panel = cashSlot.current?.closest('.balance-panel');
    const container = panel?.parentElement;
    if (!enabled || !panel || !container) {
      return;
    }
    const widths = new Map<Element, number>();
    const observer = new ResizeObserver((entries) => {
      let changed = false;
      for (const { target, contentRect } of entries) {
        if (widths.get(target) !== contentRect.width) {
          changed = widths.has(target) || changed;
          widths.set(target, contentRect.width);
        }
      }
      if (changed) {
        setSizeIndex(0);
        setFitVersion((version) => version + 1);
      }
    });
    observer.observe(container);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [cashSlot, enabled]);

  useLayoutEffect(() => {
    if (!enabled || sizeIndex >= VALUE_SIZES.length - 1) {
      return;
    }
    // No tolerance: both widths are rounded to integers, so a value overflowing by a fraction of a
    // pixel still reports a full 1px difference — and that fraction is enough to clip a digit.
    const clipped = [cashSlot, coinSlot].some((slot) => {
      const el = slot.current;
      return el ? el.scrollWidth > el.clientWidth : false;
    });
    if (clipped) {
      setSizeIndex((index) => index + 1);
    }
  }, [sizeIndex, fitVersion, resetKey, cashSlot, coinSlot, enabled]);

  return VALUE_SIZES[sizeIndex];
}
